import { Component, OnInit, ViewChild, ElementRef, AfterViewInit, Input, OnChanges, SimpleChanges } from '@angular/core';
import * as d3 from 'd3';
import { EmailByDayOfWeek, VolumeTrendItem, ClassificationSplitItem, TemplateDistributionItem, CounterpartyNegotiationItem, EscalationDistributionItem, AgingQueueItem, EscalationByCounterpartyItem, FrictionClausesItem, EscalationTrendItem, EscalationCategoriesItem } from '~/core/services/http.service';

@Component({
    selector: 'app-volume-risk-tabs',
    templateUrl: './volume-risk-tabs.component.html',
    styleUrls: ['./volume-risk-tabs.component.scss'],
    standalone: false
})
export class VolumeRiskTabsComponent implements OnInit, AfterViewInit, OnChanges {
    // ViewChild references for Volume & Pipeline charts
    @ViewChild('emailVolumeTrendChart', { static: false }) emailVolumeTrendChart!: ElementRef;
    @ViewChild('classificationSplitChart', { static: false }) classificationSplitChart!: ElementRef;
    @ViewChild('ndaTemplateChart', { static: false }) ndaTemplateChart!: ElementRef;
    @ViewChild('ndaNegotiationsChart', { static: false }) ndaNegotiationsChart!: ElementRef;

    // ViewChild references for Risk & Prediction charts
    @ViewChild('escalationDistributionChart', { static: false }) escalationDistributionChart!: ElementRef;
    @ViewChild('caseAgingQueueChart', { static: false }) caseAgingQueueChart!: ElementRef;
    @ViewChild('escalationCounterpartyChart', { static: false }) escalationCounterpartyChart!: ElementRef;
    @ViewChild('topFrictionClausesChart', { static: false }) topFrictionClausesChart!: ElementRef;
    @ViewChild('escalationTrendChart', { static: false }) escalationTrendChart!: ElementRef;
    @ViewChild('escalationReasonsChart', { static: false }) escalationReasonsChart!: ElementRef;

    @Input() emailsByDayOfWeek: EmailByDayOfWeek[] = [];
    @Input() volumeTrend: VolumeTrendItem[] = [];
    @Input() classificationSplit: ClassificationSplitItem[] = [];
    @Input() templateDistribution: TemplateDistributionItem[] = [];
    @Input() counterpartyNegotiations: CounterpartyNegotiationItem[] = [];

    @Input() escalationDistribution: EscalationDistributionItem[] = [];
    @Input() agingQueue: AgingQueueItem[] = [];
    @Input() escalationByCounterparty: EscalationByCounterpartyItem[] = [];
    @Input() frictionClauses: FrictionClausesItem[] = [];
    @Input() escalationTrend: EscalationTrendItem[] = [];
    @Input() escalationCategories: EscalationCategoriesItem[] = [];

    public activeAnalyticsTab: string = 'volume';

    constructor() {}

    ngOnInit(): void {}

    ngAfterViewInit(): void {
        // Render volume charts by default
        setTimeout(() => this.renderVolumeCharts(), 300);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ((changes['volumeTrend'] || changes['classificationSplit'] || changes['templateDistribution'] || changes['counterpartyNegotiations']) && this.activeAnalyticsTab === 'volume') {
            setTimeout(() => this.renderVolumeCharts(), 100);
        }
        if ((changes['escalationDistribution'] || changes['agingQueue'] || changes['escalationByCounterparty'] || changes['frictionClauses'] || changes['escalationTrend'] || changes['escalationCategories']) && this.activeAnalyticsTab === 'risk') {
            setTimeout(() => this.renderRiskCharts(), 100);
        }
    }

    /**
     * Select analytics tab (Volume & Pipeline or Risk & Prediction)
     */
    public selectAnalyticsTab(tab: string): void {
        this.activeAnalyticsTab = tab;
        if (tab === 'volume') {
            setTimeout(() => this.renderVolumeCharts(), 100);
        } else if (tab === 'risk') {
            setTimeout(() => this.renderRiskCharts(), 100);
        }
    }

    /**
     * Render all volume and pipeline charts
     */
    private renderVolumeCharts(): void {
        this.renderEmailVolumeTrendChart();
        this.renderClassificationSplitChart();
        this.renderNDATemplateChart();
        this.renderNDANegotiationsChart();
    }

    /**
     * Render email volume trend chart (line chart with curves) - using volume trend data
     */
    private renderEmailVolumeTrendChart(): void {
        if (!this.emailVolumeTrendChart || this.volumeTrend.length === 0) return;

        // Group data by period
        const groupedByPeriod = new Map<string, {[key: string]: number}>();
        this.volumeTrend.forEach(item => {
            if (!groupedByPeriod.has(item.period)) {
                groupedByPeriod.set(item.period, {});
            }
            groupedByPeriod.get(item.period)![item.type] = item.count;
        });

        // Convert to array for charting, sorted by date
        const data = Array.from(groupedByPeriod.entries())
            .sort((a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime())
            .map(([period, types]) => ({
                date: period,
                'NEW_NDA': types['NEW_NDA'] || 0,
                'REVIEW_NDA': types['REVIEW_NDA'] || 0,
                'OUT_OF_SCOPE': types['OUT_OF_SCOPE'] || 0
            }));

        const container = this.emailVolumeTrendChart.nativeElement;
        const containerWidth = container.clientWidth || 450;

        const svg = d3.select(container);
        svg.selectAll('*').remove();

        const margin = { top: 20, right: 20, bottom: 50, left: 50 };
        const width = (containerWidth - margin.left - margin.right) || 350;
        const height = 250;

        const svgElement = svg
            .attr('width', width + margin.left + margin.right)
            .attr('height', height + margin.top + margin.bottom)
            .attr('viewBox', `0 0 ${width + margin.left + margin.right} ${height + margin.top + margin.bottom}`)
            .style('display', 'block');

        const g = svgElement
            .append('g')
            .attr('transform', `translate(${margin.left},${margin.top})`);

        // Use time scale for better date handling
        const x = d3.scaleTime()
            .domain(d3.extent(data, d => new Date(d.date)) as [Date, Date])
            .range([0, width]);

        const y = d3.scaleLinear()
            .domain([0, d3.max(data, d => Math.max(d['NEW_NDA'], d['REVIEW_NDA'], d['OUT_OF_SCOPE'])) as number])
            .range([height, 0]);

        const colors = ['#3b82f6', '#fbbf24', '#ef4444'];
        const keys = ['NEW_NDA', 'REVIEW_NDA', 'OUT_OF_SCOPE'];

        // Add grid lines
        g.append('g')
            .attr('class', 'grid')
            .call(d3.axisLeft(y)
                .tickSize(-width)
                .tickFormat(() => '')
            )
            .style('stroke-dasharray', '4')
            .style('stroke', '#e5e7eb')
            .style('stroke-opacity', 0.5);

        // Create line generator with curve
        const line = d3.line<any>()
            .curve(d3.curveMonotoneX)
            .x(d => x(new Date(d.date)))
            .y(d => y(d.value));

        // Add lines for each category
        keys.forEach((key, i) => {
            const categoryData = data.map(d => ({ date: d.date, value: (d as any)[key] }));
            
            g.append('path')
                .datum(categoryData)
                .attr('fill', 'none')
                .attr('stroke', colors[i])
                .attr('stroke-width', 2.5)
                .attr('stroke-linejoin', 'round')
                .attr('stroke-linecap', 'round')
                .attr('d', line);
        });

        // Add X axis with rotated labels
        const xAxis = g.append('g')
            .attr('transform', `translate(0,${height})`)
            .call(d3.axisBottom(x).ticks(5))
            .style('font-size', '10px');

        xAxis.selectAll('text')
            .attr('transform', 'rotate(-45)')
            .attr('text-anchor', 'end')
            .attr('dy', '0.5em')
            .attr('dx', '-0.5em');

        // Add Y axis
        g.append('g')
            .call(d3.axisLeft(y))
            .style('font-size', '11px');

        // Add legend at top right
        const legend = svgElement.append('g')
            .attr('class', 'legend')
            .attr('transform', `translate(${width + margin.left - 80}, 15)`);

        const legendItems = legend.selectAll('.legend-item')
            .data(keys)
            .enter()
            .append('g')
            .attr('class', 'legend-item')
            .attr('transform', (d, i) => `translate(0, ${i * 16})`);

        legendItems.append('line')
            .attr('x1', 0)
            .attr('x2', 12)
            .attr('y1', 6)
            .attr('y2', 6)
            .attr('stroke', (d, i) => colors[i])
            .attr('stroke-width', 2.5);

        legendItems.append('text')
            .attr('x', 18)
            .attr('y', 10)
            .style('font-size', '10px')
            .text(d => d);
    }

    /**
     * Render classification split pie chart - using dynamic data
     */
    private renderClassificationSplitChart(): void {
        if (!this.classificationSplitChart || this.classificationSplit.length === 0) return;

        const data = this.classificationSplit;

        const container = this.classificationSplitChart.nativeElement;
        const containerWidth = container.clientWidth || 400;
        const svg = d3.select(container);
        svg.selectAll('*').remove();

        const width = containerWidth;
        const height = 280;
        const radius = Math.min(width, height) / 2 - 40;

        const svgElement = svg
            .attr('width', width)
            .attr('height', height)
            .attr('viewBox', `0 0 ${width} ${height}`)
            .style('display', 'block');

        const g = svgElement
            .append('g')
            .attr('transform', `translate(${width / 2},${height / 2 - 20})`);

        const colors = ['#3b82f6', '#fbbf24', '#9ca3af'];
        const pie = d3.pie<any>().value(d => d.count);
        const arc = d3.arc().innerRadius(radius * 0.5).outerRadius(radius);

        // Create tooltip
        const tooltip = d3.select('body')
            .append('div')
            .style('position', 'fixed')
            .style('background-color', 'white')
            .style('color', '#333')
            .style('padding', '10px 12px')
            .style('border', '1px solid #ddd')
            .style('border-radius', '4px')
            .style('font-size', '12px')
            .style('pointer-events', 'none')
            .style('opacity', 0)
            .style('box-shadow', '0 2px 8px rgba(0,0,0,0.15)')
            .style('transition', 'opacity 0.4s ease, transform 0.4s ease')
            .style('transform', 'translateY(-5px)')
            .style('z-index', 10000)
            .style('white-space', 'nowrap');

        const arcs = g.selectAll('.arc')
            .data(pie(data))
            .enter()
            .append('path')
            .attr('class', 'arc')
            .attr('d', arc as any)
            .attr('fill', (d, i) => colors[i % colors.length])
            .style('cursor', 'pointer')
            .on('mouseover', function(event, d: any) {
                d3.select(this).attr('opacity', 0.8);
                const percent = ((d.data.count / d3.sum(data, item => item.count)) * 100).toFixed(1);
                tooltip
                    .style('opacity', 1)
                    .style('transform', 'translateY(0)')
                    .style('left', (event.pageX + 10) + 'px')
                    .style('top', (event.pageY + 10) + 'px')
                    .html(`Category ${d.data.type}<br/>Count ${d.data.count}<br/>Pct (%) ${percent}`);
            })
            .on('mouseout', function() {
                d3.select(this).attr('opacity', 1);
                tooltip.style('opacity', 0).style('transform', 'translateY(-5px)');
            });

        g.selectAll('.label')
            .data(pie(data))
            .enter()
            .append('text')
            .attr('transform', d => `translate(${arc.centroid(d as any)})`)
            .attr('text-anchor', 'middle')
            .attr('dy', '0.35em')
            .style('font-size', '12px')
            .style('font-weight', 'bold')
            .style('fill', '#ffffff')
            .text(d => `${Math.round((d.data.count / d3.sum(data, d => d.count)) * 100)}%`);

        // Add legend at top right corner
        const legend = svgElement.append('g')
            .attr('class', 'legend')
            .attr('transform', `translate(${width - 140}, 15)`);

        const legendItems = legend.selectAll('.legend-item')
            .data(data)
            .enter()
            .append('g')
            .attr('class', 'legend-item')
            .attr('transform', (d, i) => `translate(0, ${i * 16})`);

        legendItems.append('rect')
            .attr('width', 12)
            .attr('height', 12)
            .attr('fill', (d, i) => colors[i % colors.length]);

        legendItems.append('text')
            .attr('x', 18)
            .attr('y', 10)
            .style('font-size', '10px')
            .text(d => d.type);
    }

    /**
     * Render NDA template distribution donut chart - using dynamic data
     */
    private renderNDATemplateChart(): void {
        if (!this.ndaTemplateChart || this.templateDistribution.length === 0) return;

        const data = this.templateDistribution.map(item => ({
            label: item.ndaType,
            value: item.count
        }));

        const container = this.ndaTemplateChart.nativeElement;
        const containerWidth = container.clientWidth || 400;
        const svg = d3.select(container);
        svg.selectAll('*').remove();

        const width = containerWidth;
        const height = 280;
        const radius = Math.min(width, height) / 2 - 40;

        const svgElement = svg
            .attr('width', width)
            .attr('height', height)
            .attr('viewBox', `0 0 ${width} ${height}`)
            .style('display', 'block');

        const g = svgElement
            .append('g')
            .attr('transform', `translate(${width / 2},${height / 2 - 20})`);

        const colors = ['#3b82f6', '#ef4444'];
        const pie = d3.pie<any>().value(d => d.value);
        const arc = d3.arc().innerRadius(radius * 0.5).outerRadius(radius);

        // Create tooltip
        const tooltip = d3.select('body')
            .append('div')
            .style('position', 'fixed')
            .style('background-color', 'white')
            .style('color', '#333')
            .style('padding', '10px 12px')
            .style('border', '1px solid #ddd')
            .style('border-radius', '4px')
            .style('font-size', '12px')
            .style('pointer-events', 'none')
            .style('opacity', 0)
            .style('box-shadow', '0 2px 8px rgba(0,0,0,0.15)')
            .style('transition', 'opacity 0.4s ease, transform 0.4s ease')
            .style('transform', 'translateY(-5px)')
            .style('z-index', 10000)
            .style('white-space', 'nowrap');

        const arcs = g.selectAll('.arc')
            .data(pie(data))
            .enter()
            .append('path')
            .attr('class', 'arc')
            .attr('d', arc as any)
            .attr('fill', (d, i) => colors[i % colors.length])
            .style('cursor', 'pointer')
            .on('mouseover', function(event, d: any) {
                d3.select(this).attr('opacity', 0.8);
                const percent = ((d.data.value / d3.sum(data, item => item.value)) * 100).toFixed(1);
                tooltip
                    .style('opacity', 1)
                    .style('transform', 'translateY(0)')
                    .style('left', (event.pageX + 10) + 'px')
                    .style('top', (event.pageY + 10) + 'px')
                    .html(`Category ${d.data.label}<br/>Count ${d.data.value}<br/>Pct (%) ${percent}`);
            })
            .on('mouseout', function() {
                d3.select(this).attr('opacity', 1);
                tooltip.style('opacity', 0).style('transform', 'translateY(-5px)');
            });

        g.selectAll('.label')
            .data(pie(data))
            .enter()
            .append('text')
            .attr('transform', d => `translate(${arc.centroid(d as any)})`)
            .attr('text-anchor', 'middle')
            .attr('dy', '0.35em')
            .style('font-size', '14px')
            .style('font-weight', 'bold')
            .style('fill', '#ffffff')
            .text(d => d.data.value);

        // Add legend at top right corner
        const legend = svgElement.append('g')
            .attr('class', 'legend')
            .attr('transform', `translate(${width - 110}, 15)`);

        const legendItems = legend.selectAll('.legend-item')
            .data(data)
            .enter()
            .append('g')
            .attr('class', 'legend-item')
            .attr('transform', (d, i) => `translate(0, ${i * 16})`);

        legendItems.append('rect')
            .attr('width', 12)
            .attr('height', 12)
            .attr('fill', (d, i) => colors[i % colors.length]);

        legendItems.append('text')
            .attr('x', 18)
            .attr('y', 10)
            .style('font-size', '10px')
            .text(d => d.label);
    }

    /**
     * Render NDA negotiations by counterparty horizontal bar chart - using dynamic data
     */
    private renderNDANegotiationsChart(): void {
        if (!this.ndaNegotiationsChart || this.counterpartyNegotiations.length === 0) return;

        // Take top 5 counterparties
        const topCounterparties = this.counterpartyNegotiations.slice(0, 5);
        const data = topCounterparties.map(item => ({
            counterparty: item.counterparty,
            chains: item.chains
        }));

        const container = this.ndaNegotiationsChart.nativeElement;
        const containerWidth = container.clientWidth || 450;
        const svg = d3.select(container);
        svg.selectAll('*').remove();

        const margin = { top: 15, right: 40, bottom: 30, left: 140 };
        const width = (containerWidth - margin.left - margin.right) || 300;
        const height = 220;

        const svgElement = svg
            .attr('width', width + margin.left + margin.right)
            .attr('height', height + margin.top + margin.bottom)
            .attr('viewBox', `0 0 ${width + margin.left + margin.right} ${height + margin.top + margin.bottom}`)
            .style('display', 'block');

        const g = svgElement
            .append('g')
            .attr('transform', `translate(${margin.left},${margin.top})`);

        const y = d3.scaleBand()
            .domain(data.map(d => d.counterparty))
            .range([height, 0])
            .padding(0.2);

        const x = d3.scaleLinear()
            .domain([0, d3.max(data, d => d.chains) as number])
            .range([0, width]);

        // Add grid lines
        g.append('g')
            .attr('class', 'grid')
            .call(d3.axisBottom(x)
                .tickSize(-height)
                .tickFormat(() => '')
            )
            .style('stroke-dasharray', '4')
            .style('stroke', '#e5e7eb')
            .style('stroke-opacity', 0.5);

        // Create tooltip for bar chart
        const barTooltip = d3.select('body')
            .append('div')
            .style('position', 'fixed')
            .style('background-color', 'white')
            .style('color', '#333')
            .style('padding', '10px 12px')
            .style('border', '1px solid #ddd')
            .style('border-radius', '4px')
            .style('font-size', '12px')
            .style('pointer-events', 'none')
            .style('opacity', 0)
            .style('box-shadow', '0 2px 8px rgba(0,0,0,0.15)')
            .style('transition', 'opacity 0.4s ease, transform 0.4s ease')
            .style('transform', 'translateY(-5px)')
            .style('z-index', 10000)
            .style('white-space', 'nowrap');

        // Add bars
        g.selectAll('.bar')
            .data(data)
            .enter()
            .append('rect')
            .attr('class', 'bar')
            .attr('x', 0)
            .attr('y', d => y(d.counterparty) as number)
            .attr('width', d => x(d.chains))
            .attr('height', y.bandwidth())
            .attr('fill', '#60a5fa')
            .style('cursor', 'pointer')
            .on('mouseover', function(event, d: any) {
                d3.select(this).attr('fill', '#3b82f6');
                barTooltip
                    .style('opacity', 1)
                    .style('transform', 'translateY(0)')
                    .style('left', (event.pageX + 10) + 'px')
                    .style('top', (event.pageY + 10) + 'px')
                    .html(`counterparty ${d.counterparty}<br/>chains ${d.chains}`);
            })
            .on('mouseout', function() {
                d3.select(this).attr('fill', '#60a5fa');
                barTooltip.style('opacity', 0).style('transform', 'translateY(-5px)');
            });

        // Add value labels on bars
        g.selectAll('.bar-label')
            .data(data)
            .enter()
            .append('text')
            .attr('class', 'bar-label')
            .attr('x', d => x(d.chains) + 5)
            .attr('y', d => (y(d.counterparty) as number) + y.bandwidth() / 2)
            .attr('dy', '0.35em')
            .style('font-size', '11px')
            .style('font-weight', 'bold')
            .text(d => d.chains.toString());

        // Add X axis
        g.append('g')
            .attr('transform', `translate(0,${height})`)
            .call(d3.axisBottom(x))
            .style('font-size', '11px');

        // Add X axis label
        svgElement.append('text')
            .attr('x', margin.left + width / 2)
            .attr('y', height + margin.top + margin.bottom - 5)
            .style('font-size', '11px')
            .style('text-anchor', 'middle')
            .style('fill', '#666')
            .text('Mail chains (unique conv_id)');

        // Add Y axis
        g.append('g')
            .call(d3.axisLeft(y))
            .style('font-size', '11px');
    }

    /**
     * Render all risk and prediction charts
     */
    private renderRiskCharts(): void {
        this.renderEscalationDistributionChart();
        this.renderCaseAgingQueueChart();
        this.renderEscalationCounterpartyChart();
        this.renderTopFrictionClausesChart();
        this.renderEscalationTrendChart();
        this.renderEscalationReasonsChart();
    }

    /**
     * Render escalation distribution donut chart
     */
    private renderEscalationDistributionChart(): void {
        if (!this.escalationDistributionChart || this.escalationDistribution.length === 0) return;

        const data = this.escalationDistribution;

        const container = this.escalationDistributionChart.nativeElement;
        const containerWidth = container.clientWidth || 400;
        const svg = d3.select(container);
        svg.selectAll('*').remove();

        const width = containerWidth;
        const height = 280;
        const radius = Math.min(width, height) / 2 - 40;

        const svgElement = svg
            .attr('width', width)
            .attr('height', height)
            .attr('viewBox', `0 0 ${width} ${height}`)
            .style('display', 'block');

        const g = svgElement
            .append('g')
            .attr('transform', `translate(${width / 2},${height / 2 - 20})`);

        const colors = ['#3b82f6', '#fbbf24', '#ef4444'];
        const escalationOrderMap = { 'NONE': 0, 'SOFT': 1, 'HARD': 2 };
        const pie = d3.pie<any>().value(d => d.count);
        const arc = d3.arc().innerRadius(radius * 0.5).outerRadius(radius);

        // Create tooltip
        const tooltip = d3.select('body')
            .append('div')
            .style('position', 'fixed')
            .style('background-color', 'white')
            .style('color', '#333')
            .style('padding', '10px 12px')
            .style('border', '1px solid #ddd')
            .style('border-radius', '4px')
            .style('font-size', '12px')
            .style('pointer-events', 'none')
            .style('opacity', 0)
            .style('box-shadow', '0 2px 8px rgba(0,0,0,0.15)')
            .style('transition', 'opacity 0.4s ease, transform 0.4s ease')
            .style('transform', 'translateY(-5px)')
            .style('z-index', 10000)
            .style('white-space', 'nowrap');

        const arcs = g.selectAll('.arc')
            .data(pie(data))
            .enter()
            .append('path')
            .attr('class', 'arc')
            .attr('d', arc as any)
            .attr('fill', (d: any) => {
                const idx = escalationOrderMap[d.data.escalation as keyof typeof escalationOrderMap] || 0;
                return colors[idx];
            })
            .style('cursor', 'pointer')
            .on('mouseover', function(event, d: any) {
                const total = d3.sum(data, (item: any) => item.count);
                const percent = ((d.data.count / total) * 100).toFixed(1);
                tooltip.html(`${d.data.escalation}<br/>Count ${d.data.count}<br/>Pct (%) ${percent}`)
                    .style('opacity', 1)
                    .style('transform', 'translateY(0)');
                d3.select(this).style('opacity', 0.8);
            })
            .on('mousemove', function(event) {
                tooltip.style('left', (event.pageX + 10) + 'px').style('top', (event.pageY + 10) + 'px');
            })
            .on('mouseout', function() {
                tooltip.style('opacity', 0).style('transform', 'translateY(-5px)');
                d3.select(this).style('opacity', 1);
            });

        g.selectAll('.label')
            .data(pie(data))
            .enter()
            .append('text')
            .attr('transform', d => `translate(${arc.centroid(d as any)})`)
            .attr('text-anchor', 'middle')
            .attr('dy', '0.35em')
            .style('font-size', '12px')
            .style('font-weight', 'bold')
            .style('fill', '#ffffff')
            .text(d => `${Math.round((d.data.count / d3.sum(data, d => d.count)) * 100)}%`);

        // Add legend at top right corner
        const legend = svgElement.append('g')
            .attr('class', 'legend')
            .attr('transform', `translate(${width - 120}, 15)`);

        const legendItems = legend.selectAll('.legend-item')
            .data(data)
            .enter()
            .append('g')
            .attr('class', 'legend-item')
            .attr('transform', (d, i) => `translate(0, ${i * 16})`);

        legendItems.append('rect')
            .attr('width', 12)
            .attr('height', 12)
            .attr('fill', (d: any) => {
                const idx = escalationOrderMap[d.escalation as keyof typeof escalationOrderMap] || 0;
                return colors[idx];
            });

        legendItems.append('text')
            .attr('x', 18)
            .attr('y', 10)
            .style('font-size', '10px')
            .text(d => d.escalation);
    }

    /**
     * Render case aging queue stacked bar chart
     */
    private renderCaseAgingQueueChart(): void {
        if (!this.caseAgingQueueChart || this.agingQueue.length === 0) return;

        // Group aging queue data by age bucket
        const grouped = new Map<string, {[key: string]: number}>();
        const ageOrder = ['0-2d', '3-7d', '8-14d', '15+d'];
        
        this.agingQueue.forEach(item => {
            if (!grouped.has(item.age)) {
                grouped.set(item.age, {});
            }
            grouped.get(item.age)![item.escalation] = item.count;
        });

        // Convert to array with proper ordering
        const data = ageOrder
            .filter(age => grouped.has(age))
            .map(age => ({
                age: age,
                'NONE': grouped.get(age)?.['NONE'] || 0,
                'SOFT': grouped.get(age)?.['SOFT'] || 0,
                'HARD': grouped.get(age)?.['HARD'] || 0
            }));

        const container = this.caseAgingQueueChart.nativeElement;
        const containerWidth = container.clientWidth || 450;
        const svg = d3.select(container);
        svg.selectAll('*').remove();

        const margin = { top: 50, right: 40, bottom: 30, left: 60 };
        const width = (containerWidth - margin.left - margin.right) || 350;
        const height = 180;

        const svgElement = svg
            .attr('width', width + margin.left + margin.right)
            .attr('height', height + margin.top + margin.bottom)
            .attr('viewBox', `0 0 ${width + margin.left + margin.right} ${height + margin.top + margin.bottom}`)
            .style('display', 'block');

        const g = svgElement
            .append('g')
            .attr('transform', `translate(${margin.left},${margin.top})`);

        const y = d3.scaleBand()
            .domain(data.map(d => d.age))
            .range([height, 0])
            .padding(0.2);

        // Calculate max value for X scale
        const maxValue = d3.max(data, d => (d['NONE'] || 0) + (d['SOFT'] || 0) + (d['HARD'] || 0)) || 100;
        const x = d3.scaleLinear()
            .domain([0, maxValue])
            .range([0, width]);

        const colors = ['#3b82f6', '#fbbf24', '#ef4444'];
        const escalations = ['NONE', 'SOFT', 'HARD'];

        // Add grid lines
        g.append('g')
            .attr('class', 'grid')
            .call(d3.axisBottom(x)
                .tickSize(-height)
                .tickFormat(() => '')
            )
            .style('stroke-dasharray', '4')
            .style('stroke', '#e5e7eb')
            .style('stroke-opacity', 0.5);

        // Create tooltip for stacked bars
        const barTooltip = d3.select('body')
            .append('div')
            .style('position', 'fixed')
            .style('background-color', 'white')
            .style('color', '#333')
            .style('padding', '10px 12px')
            .style('border', '1px solid #ddd')
            .style('border-radius', '4px')
            .style('font-size', '12px')
            .style('pointer-events', 'none')
            .style('opacity', 0)
            .style('box-shadow', '0 2px 8px rgba(0,0,0,0.15)')
            .style('transition', 'opacity 0.4s ease, transform 0.4s ease')
            .style('transform', 'translateY(-5px)')
            .style('z-index', 10000)
            .style('white-space', 'nowrap');

        // Create stacked bars
        const stack = d3.stack<any>().keys(escalations);
        const stackedData = stack(data);

        g.selectAll('.escalationGroup')
            .data(stackedData)
            .enter()
            .append('g')
            .attr('class', 'escalationGroup')
            .attr('fill', (d, i) => colors[i])
            .selectAll('rect')
            .data(d => d)
            .enter()
            .append('rect')
            .attr('x', d => x(d[0]))
            .attr('y', d => y(d.data.age) as number)
            .attr('width', d => x(d[1]) - x(d[0]))
            .attr('height', y.bandwidth())
            .style('cursor', 'pointer')
            .on('mouseover', function(event, d: any) {
                const escalation = escalations[stackedData.indexOf(d3.select(this.parentNode as any).datum() as any)];
                const value = d[1] - d[0];
                barTooltip.html(`Age: ${d.data.age}<br/>${escalation}: ${value}`)
                    .style('opacity', 1)
                    .style('transform', 'translateY(0)');
                d3.select(this).style('opacity', 0.8);
            })
            .on('mousemove', function(event) {
                barTooltip.style('left', (event.pageX + 10) + 'px').style('top', (event.pageY + 10) + 'px');
            })
            .on('mouseout', function() {
                barTooltip.style('opacity', 0).style('transform', 'translateY(-5px)');
                d3.select(this).style('opacity', 1);
            });

        // Add X axis
        g.append('g')
            .attr('transform', `translate(0,${height})`)
            .call(d3.axisBottom(x).ticks(5))
            .style('font-size', '11px');

        // Add Y axis
        g.append('g')
            .call(d3.axisLeft(y))
            .style('font-size', '11px');

        // Add legend (positioned at top-right corner)
        const legend = svgElement.append('g')
            .attr('class', 'legend')
            .attr('transform', `translate(${width + margin.left - 120}, 5)`);

        const legendItems = legend.selectAll('.legend-item')
            .data(escalations)
            .enter()
            .append('g')
            .attr('class', 'legend-item')
            .attr('transform', (d, i) => `translate(0, ${i * 16})`);

        legendItems.append('rect')
            .attr('width', 12)
            .attr('height', 12)
            .attr('fill', (d, i) => colors[i]);

        legendItems.append('text')
            .attr('x', 18)
            .attr('y', 10)
            .style('font-size', '10px')
            .text(d => d);
    }

    /**
     * Render escalation by counterparty horizontal stacked bar chart (Top 12)
     */
    private renderEscalationCounterpartyChart(): void {
        if (!this.escalationCounterpartyChart || this.escalationByCounterparty.length === 0) return;

        // Group by counterparty
        const grouped = new Map<string, {[key: string]: number}>();
        this.escalationByCounterparty.forEach(item => {
            if (!grouped.has(item.counterparty)) {
                grouped.set(item.counterparty, {});
            }
            grouped.get(item.counterparty)![item.escalation] = item.count;
        });

        // Convert to array and get top 12
        const data = Array.from(grouped.entries())
            .map(([cp, escalations]) => ({
                counterparty: cp,
                'NONE': escalations['NONE'] || 0,
                'SOFT': escalations['SOFT'] || 0,
                'HARD': escalations['HARD'] || 0
            }))
            .sort((a, b) => ((b['HARD'] || 0) + (b['SOFT'] || 0)) - ((a['HARD'] || 0) + (a['SOFT'] || 0)))
            .slice(0, 12);

        const container = this.escalationCounterpartyChart.nativeElement;
        const containerWidth = container.clientWidth || 450;
        const svg = d3.select(container);
        svg.selectAll('*').remove();

        const margin = { top: 50, right: 40, bottom: 30, left: 140 };
        const width = (containerWidth - margin.left - margin.right) || 300;
        const height = Math.max(220, data.length * 18);

        const svgElement = svg
            .attr('width', width + margin.left + margin.right)
            .attr('height', height + margin.top + margin.bottom)
            .attr('viewBox', `0 0 ${width + margin.left + margin.right} ${height + margin.top + margin.bottom}`)
            .style('display', 'block');

        const g = svgElement
            .append('g')
            .attr('transform', `translate(${margin.left},${margin.top})`);

        const y = d3.scaleBand()
            .domain(data.map(d => d.counterparty))
            .range([height, 0])
            .padding(0.2);

        // Calculate max value for X scale
        const maxValue = d3.max(data, d => (d['NONE'] || 0) + (d['SOFT'] || 0) + (d['HARD'] || 0)) || 100;
        const x = d3.scaleLinear()
            .domain([0, maxValue])
            .range([0, width]);

        const colors = ['#3b82f6', '#fbbf24', '#ef4444'];
        const escalations = ['NONE', 'SOFT', 'HARD'];

        // Create tooltip for stacked bars
        const barTooltip = d3.select('body')
            .append('div')
            .style('position', 'fixed')
            .style('background-color', 'white')
            .style('color', '#333')
            .style('padding', '10px 12px')
            .style('border', '1px solid #ddd')
            .style('border-radius', '4px')
            .style('font-size', '12px')
            .style('pointer-events', 'none')
            .style('opacity', 0)
            .style('box-shadow', '0 2px 8px rgba(0,0,0,0.15)')
            .style('transition', 'opacity 0.4s ease, transform 0.4s ease')
            .style('transform', 'translateY(-5px)')
            .style('z-index', 10000)
            .style('white-space', 'nowrap');

        // Add grid lines
        g.append('g')
            .attr('class', 'grid')
            .call(d3.axisBottom(x)
                .tickSize(-height)
                .tickFormat(() => '')
            )
            .style('stroke-dasharray', '4')
            .style('stroke', '#e5e7eb')
            .style('stroke-opacity', 0.5);

        // Create stacked bars
        const stack = d3.stack<any>().keys(escalations);
        const stackedData = stack(data);

        g.selectAll('.escalationGroup')
            .data(stackedData)
            .enter()
            .append('g')
            .attr('class', 'escalationGroup')
            .attr('fill', (d, i) => colors[i])
            .selectAll('rect')
            .data(d => d)
            .enter()
            .append('rect')
            .attr('x', d => x(d[0]))
            .attr('y', d => y(d.data.counterparty) as number)
            .attr('width', d => x(d[1]) - x(d[0]))
            .attr('height', y.bandwidth())
            .style('cursor', 'pointer')
            .on('mouseover', function(event, d: any) {
                const escalation = escalations[stackedData.indexOf(d3.select(this.parentNode as any).datum() as any)];
                const value = d[1] - d[0];
                barTooltip.html(`Counterparty: ${d.data.counterparty}<br/>${escalation}: ${value}`)
                    .style('opacity', 1)
                    .style('transform', 'translateY(0)');
                d3.select(this).style('opacity', 0.8);
            })
            .on('mousemove', function(event) {
                barTooltip.style('left', (event.pageX + 10) + 'px').style('top', (event.pageY + 10) + 'px');
            })
            .on('mouseout', function() {
                barTooltip.style('opacity', 0).style('transform', 'translateY(-5px)');
                d3.select(this).style('opacity', 1);
            });

        // Add X axis
        g.append('g')
            .attr('transform', `translate(0,${height})`)
            .call(d3.axisBottom(x).ticks(5))
            .style('font-size', '11px');

        // Add Y axis
        g.append('g')
            .call(d3.axisLeft(y))
            .style('font-size', '9px');

        // Add legend (positioned at top-right corner)
        const legend = svgElement.append('g')
            .attr('class', 'legend')
            .attr('transform', `translate(${width + margin.left - 120}, 5)`);

        const legendItems = legend.selectAll('.legend-item')
            .data(escalations)
            .enter()
            .append('g')
            .attr('class', 'legend-item')
            .attr('transform', (d, i) => `translate(0, ${i * 16})`);

        legendItems.append('rect')
            .attr('width', 12)
            .attr('height', 12)
            .attr('fill', (d, i) => colors[i]);

        legendItems.append('text')
            .attr('x', 18)
            .attr('y', 10)
            .style('font-size', '10px')
            .text(d => d);
    }

    /**
     * Render top friction clauses horizontal bar chart
     */
    private renderTopFrictionClausesChart(): void {
        if (!this.topFrictionClausesChart || this.frictionClauses.length === 0) return;

        // Get top 12 friction clauses (rules)
        const data = this.frictionClauses
            .sort((a, b) => b.events - a.events)
            .slice(0, 12)
            .map(d => ({
                label: d.ruleId + ' ' + (d.ruleName !== '(name not found)' ? d.ruleName : ''),
                events: d.events
            }));

        const container = this.topFrictionClausesChart.nativeElement;
        const containerWidth = container.clientWidth || 450;
        const svg = d3.select(container);
        svg.selectAll('*').remove();

        const margin = { top: 15, right: 40, bottom: 30, left: 60 };
        const width = (containerWidth - margin.left - margin.right) || 350;
        const height = Math.max(220, data.length * 18);

        const svgElement = svg
            .attr('width', width + margin.left + margin.right)
            .attr('height', height + margin.top + margin.bottom)
            .attr('viewBox', `0 0 ${width + margin.left + margin.right} ${height + margin.top + margin.bottom}`)
            .style('display', 'block');

        const g = svgElement
            .append('g')
            .attr('transform', `translate(${margin.left},${margin.top})`);

        const y = d3.scaleBand()
            .domain(data.map(d => d.label))
            .range([height, 0])
            .padding(0.2);

        const maxEvents = d3.max(data, d => d.events) || 100;
        const x = d3.scaleLinear()
            .domain([0, maxEvents])
            .range([0, width]);

        // Add grid lines
        g.append('g')
            .attr('class', 'grid')
            .call(d3.axisBottom(x)
                .tickSize(-height)
                .tickFormat(() => '')
            )
            .style('stroke-dasharray', '4')
            .style('stroke', '#e5e7eb')
            .style('stroke-opacity', 0.5);

        // Create tooltip for bar chart
        const barTooltip = d3.select('body')
            .append('div')
            .style('position', 'fixed')
            .style('background-color', 'white')
            .style('color', '#333')
            .style('padding', '10px 12px')
            .style('border', '1px solid #ddd')
            .style('border-radius', '4px')
            .style('font-size', '12px')
            .style('pointer-events', 'none')
            .style('opacity', 0)
            .style('box-shadow', '0 2px 8px rgba(0,0,0,0.15)')
            .style('transition', 'opacity 0.4s ease, transform 0.4s ease')
            .style('transform', 'translateY(-5px)')
            .style('z-index', 10000)
            .style('white-space', 'nowrap');

        // Add bars
        g.selectAll('.bar')
            .data(data)
            .enter()
            .append('rect')
            .attr('class', 'bar')
            .attr('x', 0)
            .attr('y', d => y(d.label) as number)
            .attr('width', d => x(d.events))
            .attr('height', y.bandwidth())
            .attr('fill', '#60a5fa')
            .style('cursor', 'pointer')
            .on('mouseover', function(event, d: any) {
                barTooltip.html(`${d.label}<br/>Events ${d.events}`)
                    .style('opacity', 1)
                    .style('transform', 'translateY(0)');
                d3.select(this).style('opacity', 0.8);
            })
            .on('mousemove', function(event) {
                barTooltip.style('left', (event.pageX + 10) + 'px').style('top', (event.pageY + 10) + 'px');
            })
            .on('mouseout', function() {
                barTooltip.style('opacity', 0).style('transform', 'translateY(-5px)');
                d3.select(this).style('opacity', 1);
            });

        // Add value labels
        g.selectAll('.bar-label')
            .data(data)
            .enter()
            .append('text')
            .attr('class', 'bar-label')
            .attr('x', d => x(d.events) + 5)
            .attr('y', d => (y(d.label) as number) + y.bandwidth() / 2)
            .attr('dy', '0.35em')
            .style('font-size', '11px')
            .style('font-weight', 'bold')
            .text(d => d.events.toString());

        // Add X axis
        g.append('g')
            .attr('transform', `translate(0,${height})`)
            .call(d3.axisBottom(x).ticks(5))
            .style('font-size', '11px');

        // Add Y axis
        g.append('g')
            .call(d3.axisLeft(y))
            .style('font-size', '9px');
    }

    /**
     * Render escalation trend over time stacked area chart
     */
    private renderEscalationTrendChart(): void {
        if (!this.escalationTrendChart || this.escalationTrend.length === 0) return;

        // Group data by period
        const groupedByPeriod = new Map<string, {[key: string]: number}>();
        this.escalationTrend.forEach(item => {
            if (!groupedByPeriod.has(item.period)) {
                groupedByPeriod.set(item.period, {});
            }
            groupedByPeriod.get(item.period)![item.escalation] = item.count;
        });

        // Convert to array for charting, sorted by date
        const data = Array.from(groupedByPeriod.entries())
            .sort((a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime())
            .map(([period, escalations]) => ({
                date: period,
                'NONE': escalations['NONE'] || 0,
                'SOFT': escalations['SOFT'] || 0,
                'HARD': escalations['HARD'] || 0
            }));

        const container = this.escalationTrendChart.nativeElement;
        const containerWidth = container.clientWidth || 450;
        const svg = d3.select(container);
        svg.selectAll('*').remove();

        const margin = { top: 50, right: 40, bottom: 50, left: 50 };
        const width = (containerWidth - margin.left - margin.right) || 350;
        const height = 250;

        const svgElement = svg
            .attr('width', width + margin.left + margin.right)
            .attr('height', height + margin.top + margin.bottom)
            .attr('viewBox', `0 0 ${width + margin.left + margin.right} ${height + margin.top + margin.bottom}`)
            .style('display', 'block');

        const g = svgElement
            .append('g')
            .attr('transform', `translate(${margin.left},${margin.top})`);

        const x = d3.scaleTime()
            .domain(d3.extent(data, d => new Date(d.date)) as [Date, Date])
            .range([0, width]);

        const y = d3.scaleLinear()
            .domain([0, d3.max(data, d => (d['NONE'] || 0) + (d['SOFT'] || 0) + (d['HARD'] || 0)) as number])
            .range([height, 0]);

        const colors = ['#3b82f6', '#fbbf24', '#ef4444'];
        const keys = ['NONE', 'SOFT', 'HARD'];

        // Add grid lines
        g.append('g')
            .attr('class', 'grid')
            .call(d3.axisLeft(y)
                .tickSize(-width)
                .tickFormat(() => '')
            )
            .style('stroke-dasharray', '4')
            .style('stroke', '#e5e7eb')
            .style('stroke-opacity', 0.5);

        // Create stack
        const stack = d3.stack<any>().keys(keys);
        const stackedData = stack(data);

        // Create area generator with smooth curves
        const area = d3.area<any>()
            .x(d => x(new Date(d.data.date)))
            .y0(d => y(d[0]))
            .y1(d => y(d[1]))
            .curve(d3.curveMonotoneX);

        // Add areas
        g.selectAll('.area')
            .data(stackedData)
            .enter()
            .append('path')
            .attr('class', 'area')
            .attr('d', area as any)
            .attr('fill', (d, i) => colors[i % colors.length])
            .attr('opacity', 0.7)
            .style('stroke', 'none');

        // Add X axis with rotated labels
        const xAxis = g.append('g')
            .attr('transform', `translate(0,${height})`)
            .call(d3.axisBottom(x).ticks(5))
            .style('font-size', '10px');

        xAxis.selectAll('text')
            .attr('transform', 'rotate(-45)')
            .attr('text-anchor', 'end')
            .attr('dy', '0.5em')
            .attr('dx', '-0.5em');

        // Add Y axis
        g.append('g')
            .call(d3.axisLeft(y))
            .style('font-size', '11px');

        // Add legend at top-right corner
        const legend = svgElement.append('g')
            .attr('class', 'legend')
            .attr('transform', `translate(${width + margin.left + margin.right - 90}, 15)`);

        const legendItems = legend.selectAll('.legend-item')
            .data(keys)
            .enter()
            .append('g')
            .attr('class', 'legend-item')
            .attr('transform', (d, i) => `translate(0, ${i * 16})`);

        legendItems.append('rect')
            .attr('width', 12)
            .attr('height', 12)
            .attr('fill', (d, i) => colors[i]);

        legendItems.append('text')
            .attr('x', 18)
            .attr('y', 10)
            .style('font-size', '10px')
            .text(d => d);
    }

    /**
     * Render escalation reasons horizontal stacked bar chart (Hard & Soft)
     */
    private renderEscalationReasonsChart(): void {
        if (!this.escalationReasonsChart || this.escalationCategories.length === 0) return;

        // Group by category and aggregate hard/soft levels
        const grouped = new Map<string, {[key: string]: number}>();
        this.escalationCategories.forEach(item => {
            if (!grouped.has(item.category)) {
                grouped.set(item.category, {});
            }
            grouped.get(item.category)![item.level] = item.count;
        });

        // Convert to array and sort by total descending
        const data = Array.from(grouped.entries())
            .map(([category, levels]) => ({
                category: category,
                'HARD': levels['HARD'] || 0,
                'SOFT': levels['SOFT'] || 0
            }))
            .sort((a, b) => ((b['HARD'] || 0) + (b['SOFT'] || 0)) - ((a['HARD'] || 0) + (a['SOFT'] || 0)));

        const container = this.escalationReasonsChart.nativeElement;
        const containerWidth = container.clientWidth || 450;
        const svg = d3.select(container);
        svg.selectAll('*').remove();

        const margin = { top: 50, right: 40, bottom: 30, left: 140 };
        const width = (containerWidth - margin.left - margin.right) || 300;
        const height = Math.max(220, data.length * 18);

        const svgElement = svg
            .attr('width', width + margin.left + margin.right)
            .attr('height', height + margin.top + margin.bottom)
            .attr('viewBox', `0 0 ${width + margin.left + margin.right} ${height + margin.top + margin.bottom}`)
            .style('display', 'block');

        const g = svgElement
            .append('g')
            .attr('transform', `translate(${margin.left},${margin.top})`);

        const y = d3.scaleBand()
            .domain(data.map(d => d.category))
            .range([height, 0])
            .padding(0.2);

        // Calculate max value for X scale
        const maxValue = d3.max(data, d => (d['HARD'] || 0) + (d['SOFT'] || 0)) || 100;
        const x = d3.scaleLinear()
            .domain([0, maxValue])
            .range([0, width]);

        const colors = ['#cc0000', '#ff9900'];
        const levels = ['HARD', 'SOFT'];

        // Create tooltip for stacked bars
        const barTooltip = d3.select('body')
            .append('div')
            .style('position', 'fixed')
            .style('background-color', 'white')
            .style('color', '#333')
            .style('padding', '10px 12px')
            .style('border', '1px solid #ddd')
            .style('border-radius', '4px')
            .style('font-size', '12px')
            .style('pointer-events', 'none')
            .style('opacity', 0)
            .style('box-shadow', '0 2px 8px rgba(0,0,0,0.15)')
            .style('transition', 'opacity 0.4s ease, transform 0.4s ease')
            .style('transform', 'translateY(-5px)')
            .style('z-index', 10000)
            .style('white-space', 'nowrap');

        // Add grid lines
        g.append('g')
            .attr('class', 'grid')
            .call(d3.axisBottom(x)
                .tickSize(-height)
                .tickFormat(() => '')
            )
            .style('stroke-dasharray', '4')
            .style('stroke', '#e5e7eb')
            .style('stroke-opacity', 0.5);

        // Create stacked bars
        const stack = d3.stack<any>().keys(levels);
        const stackedData = stack(data);

        g.selectAll('.levelGroup')
            .data(stackedData)
            .enter()
            .append('g')
            .attr('class', 'levelGroup')
            .attr('fill', (d, i) => colors[i])
            .selectAll('rect')
            .data(d => d)
            .enter()
            .append('rect')
            .attr('x', d => x(d[0]))
            .attr('y', d => y(d.data.category) as number)
            .attr('width', d => x(d[1]) - x(d[0]))
            .attr('height', y.bandwidth())
            .style('cursor', 'pointer')
            .on('mouseover', function(event, d: any) {
                const level = levels[stackedData.indexOf(d3.select(this.parentNode as any).datum() as any)];
                const value = d[1] - d[0];
                barTooltip.html(`Category: ${d.data.category}<br/>${level}: ${value}`)
                    .style('opacity', 1)
                    .style('transform', 'translateY(0)');
                d3.select(this).style('opacity', 0.8);
            })
            .on('mousemove', function(event) {
                barTooltip.style('left', (event.pageX + 10) + 'px').style('top', (event.pageY + 10) + 'px');
            })
            .on('mouseout', function() {
                barTooltip.style('opacity', 0).style('transform', 'translateY(-5px)');
                d3.select(this).style('opacity', 1);
            });

        // Add X axis
        g.append('g')
            .attr('transform', `translate(0,${height})`)
            .call(d3.axisBottom(x).ticks(5))
            .style('font-size', '11px');

        // Add Y axis
        g.append('g')
            .call(d3.axisLeft(y))
            .style('font-size', '9px');

        // Add legend (positioned at top-right corner)
        const legend = svgElement.append('g')
            .attr('class', 'legend')
            .attr('transform', `translate(${width + margin.left - 100}, 5)`);

        const legendItems = legend.selectAll('.legend-item')
            .data(levels)
            .enter()
            .append('g')
            .attr('class', 'legend-item')
            .attr('transform', (d, i) => `translate(0, ${i * 16})`);

        legendItems.append('rect')
            .attr('width', 12)
            .attr('height', 12)
            .attr('fill', (d, i) => colors[i]);

        legendItems.append('text')
            .attr('x', 18)
            .attr('y', 10)
            .style('font-size', '10px')
            .text(d => d);
    }
}
